/* ==========================================================================
   rental-gpe-widget.js – v7 (Improved)
   - Amber "Request Availability" for hopeful but not confirmed
   - Combined label approach: blue line serves as field labels
   - Smaller price text and usage note
   - More compact inputs (34px height)
   - Enhanced toast notifications
   - Half-Day click = +5 hours (not 4)
   - Holidays/closed dates act like Sunday everywhere
   - Weekend note logic per spec
   - Local-date fmtYMD (no UTC drift)
   ========================================================================== */

/* -------------------------------------------------------------------------- */
/* 0 · Date Library & Global Helpers                                          */
/* -------------------------------------------------------------------------- */
const {
  addDays, addHours, differenceInMinutes,
  isSameDay, isSaturday, isSunday
} = dateFns;

const DOW3    = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const MONTHS3 = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const NBSP    = '\u00A0';
const NNBSP   = '\u202F';
const NBHYPH  = '\u2011';
const TIMES   = '\u00D7'; // ×

// LOCAL yyyy-mm-dd (no UTC drift)
const fmtYMD = (d) =>
  `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

const to12hm = (d) => {
  let h = d.getHours(), m = d.getMinutes();
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12; if (h === 0) h = 12;
  return `${h}:${String(m).padStart(2,'0')}${NBSP}${ampm}`;
};
const toDowTime = (d) => `${DOW3[d.getDay()]} ${d.getHours()%12||12}${d.getMinutes()?':'+String(d.getMinutes()).padStart(2,'0'):''}${NBSP}${d.getHours()<12?'AM':'PM'}`;
const toMonDowTime = (d) => `${DOW3[d.getDay()]} ${MONTHS3[d.getMonth()]} ${d.getDate()} · ${to12hm(d)}`;
function ymdNum(d){ return +fmtYMD(d).replace(/-/g,''); }
function crossesClosedDate(s,e){
  const sN = ymdNum(s), eN = ymdNum(e);
  return CLOSED_DATES.some(ymd => {
    const n = +String(ymd).replace(/-/g,'');
    return n >= sN && n <= eN;
  });
}
function rentalDaysExact(s,e){
  return differenceInMinutes(e,s) / 1440;
}
const money0 = (n) => `$${Math.round(n).toLocaleString()}`;
const qtyFmt  = (q) => {
  const n=Number(q); const r=Math.round(n*100)/100;
  return Number.isInteger(r)? String(r) : r.toFixed(2);
};
const fmtUnit = (raw) => {
  const t=String(raw).replace(/\s+/g,' ').trim();
  if (/^1\/?2|½/i.test(t)) return `½${NBHYPH}Day`;
  if (/^14\b/i.test(t))    return `14${NBHYPH}Day`;
  if (/^28\b/i.test(t))    return `28${NBHYPH}Day`;
  return t.replace(/\bDay\b/,'Day').replace(/\bWeek\b/,'Week');
};

/* Closed dates metafield (shop-level). In theme, expose:
<script>window.CLOSED_DATES = {{ shop.metafields.gpe.closed_dates | json }};</script> */
const CLOSED_DATES = (Array.isArray(window.CLOSED_DATES) ? window.CLOSED_DATES : [])
  .map(s => String(s).slice(0,10));

const isClosedCalendarDay = (d) => isSunday(d) || CLOSED_DATES.includes(fmtYMD(d));

function nextOpenMorning(from){
  let dt=new Date(from);
  // start from next day if we're before 08:00 and the same day is closed
  if (isClosedCalendarDay(dt)) dt = addDays(dt, 0);
  dt.setHours(8,0,0,0);
  while (isClosedCalendarDay(dt)) {
    dt = addDays(dt, 1);
    dt.setHours(8,0,0,0);
  }
  return dt;
}

function hoursForDate(d){
  if (isClosedCalendarDay(d)) return null;
  if (isSaturday(d)) return [7,17];  // 7:00 AM to 5:00 PM
  return [6,18];  // 6:00 AM to 6:00 PM Mon-Fri
}

/* Weekend special rules */
function weekendApplied(s,e){
  const isFriAfter4 = (s.getDay()===5 && s.getHours()>=16);
  const isSat = (s.getDay()===6);

  const endMonBefore8 = (e.getDay()===1 && (e.getHours()<8 || (e.getHours()===8 && e.getMinutes()===0)));
  const endAnySat = (e.getDay()===6);
  return (isFriAfter4 || isSat) && (endMonBefore8 || endAnySat);
}
function weekendNear(s,e){
  const friNoonOrAfter = (s.getDay()===5 && s.getHours()>=12);
  const isSat = (s.getDay()===6);
  const monNoonOrBefore = (e.getDay()===1 && (e.getHours()<12 || (e.getHours()===12 && e.getMinutes()===0)));
  return (friNoonOrAfter || isSat) && (monNoonOrBefore || e.getDay()===6);
}
/* Holiday weekend special rules */
function holidayWeekendApplied(s,e){
  const isFriAfter4 = (s.getDay()===5 && s.getHours()>=16);
  const isSat = (s.getDay()===6);

  // check if Monday is a closed day (holiday or Sunday)
  const monday = addDays(s, (1 + 7 - s.getDay()) % 7); // next Monday after pickup
  const mondayIsHoliday = isClosedCalendarDay(monday);

  const endTueBefore8 = (e.getDay()===2 && (e.getHours()<8 || (e.getHours()===8 && e.getMinutes()===0)));

  return mondayIsHoliday && (isFriAfter4 || isSat) && endTueBefore8;
}



/* -------------------------------------------------------------------------- */
/* 1 · CSS                                                                    */
/* -------------------------------------------------------------------------- */
const GPE_INLINE_CSS = `
:root { 
  --gpe-accent:#0ea5e9; 
  --gpe-note:#dbeafe; 
  --gpe-note-text:#075985;
  --gpe-amber:#f59e0b;  /* New amber for request state */
}
.gpe-wrap{--ring:#fde68a}

/* Combined label row (blue pickup/return indicators) */
.gpe-label-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  padding: 8px 8px 4px;
  align-items: center;
}
.gpe-label-row .pickup-label,
.gpe-label-row .return-label {
  font-size: 0.92rem;
  font-weight: 600;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 6px;
}
.gpe-label-row .pickup-label { grid-column: 1 / span 2; }
.gpe-label-row .return-label { grid-column: 3 / span 2; }
.gpe-label-row .time-preview {
  color: var(--gpe-accent);
  font-weight: 700;
}

/* Fields - more compact */
.gpe-fields-row{
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  padding: 2px 8px 6px;
}
.gpe-field input,
.gpe-field select{
  width: 100%;
  height: 34px;  /* Reduced from 38px */
  padding: 4px 8px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.92rem;
  background: #fff;
  box-sizing: border-box;
}

@media (max-width: 640px){
  .gpe-label-row {
    grid-template-columns: 1fr 1fr;
    padding: 8px 8px 2px;
  }
  .gpe-label-row .pickup-label { 
    grid-column: 1; 
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }
  .gpe-label-row .return-label { 
    grid-column: 2; 
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }
  .gpe-label-row .time-preview {
    font-size: 0.88rem;
  }
  .gpe-fields-row{
    grid-template-columns: 1fr 1fr;
    gap: 6px;
    padding: 0 8px 6px;
  }
  .gpe-field:nth-child(1),
  .gpe-field:nth-child(3) {
    grid-column: 1;
  }
  .gpe-field:nth-child(2),
  .gpe-field:nth-child(4) {
    grid-column: 2;
  }
}

/* CTA row + note + estimate */
.gpe-cta-row{
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 10px;
  align-items: center;
  margin: 8px;
}
.gpe-cta{
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 34px;  /* Reduced from 36px */
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  background: #111827;
  color: #fff;
  cursor: pointer;
  font-size: 0.92rem;
}
.gpe-cta.available{background:#16a34a}
.gpe-cta.checking{background:#374151}
.gpe-cta.unavailable{background:#2563eb}
.gpe-cta.idle{background:#2563eb}

.gpe-note{
  justify-self: start;
  background: var(--gpe-note);
  color: var(--gpe-note-text);
  padding: 7px 11px;
  border-radius: 10px;
  font-size: 0.88rem;
  display: none;
}
.gpe-note.show{display:block}

/* Price display - smaller text */
.gpe-price-full{
  display: flex;
  gap: 8px;
  align-items: baseline;
  white-space: nowrap;
  font-size: 0.88rem;  /* Smaller price text */
  direction: ltr;
}
.gpe-price-left{
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  direction: rtl;  /* Right-to-left to show end of content */
  text-align: left;
}
.gpe-price-left > span {
  direction: ltr;  /* Reset inner content to left-to-right */
  unicode-bidi: bidi-override;
}
.gpe-price-right{
  flex: 0 0 auto;
  font-weight: 600;
}
.gpe-price-line1,
.gpe-price-line2{display:inline}
.gpe-price-line2{opacity:.9}

/* Usage note - smaller and lighter */
.gpe-usage-note{
  margin: 2px 8px 0;
  color: #94a3b8;  /* Lighter gray */
  font-size: 0.8rem;  /* Smaller */
}

/* Rates row - slightly more compact */
.gpe-rate-strip{
  position: relative;
  display: flex;
  gap: 0;
  border: 1px solid #dee2e6;
  border-radius: 10px;
  overflow: hidden;
  margin: 4px;
}
.rate-card{
  flex: 1;
  padding: 8px 10px;  /* Reduced padding */
  text-align: center;
  cursor: pointer;
  user-select: none;
}
.rate-card + .rate-card{border-left:1px solid #dee2e6}
.rate-price{
  display: block;
  font-size: 1.05rem;  /* Slightly smaller */
  font-weight: 800;
  margin-bottom: 2px;
}
.rate-label{
  display: flex;
  gap: 6px;
  justify-content: center;
  align-items: center;
  font-size: 0.76rem;
  color: #334155;
}
.rate-label .drop{
  font-size: 0.9rem;
  opacity: 0.65;
  cursor: pointer;
}

/* Shift matrix */
.gpe-shift-matrix{
  display: none;
  margin: 6px 8px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}
.gpe-shift-matrix table{
  width: 100%;
  border-collapse: collapse;
  font-size: 0.84rem;
}
.gpe-shift-matrix thead{background:#f8fafc}
.gpe-shift-matrix th,
.gpe-shift-matrix td{padding:7px}
.gpe-shift-matrix td+td,
.gpe-shift-matrix th+th{text-align:right}

/* Toast - enhanced styling */
#gpe-toast{
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  bottom: 22px;
  background: #111827;
  color: #fff;
  padding: 10px 16px;
  border-radius: 10px;
  font-size: 0.9rem;
  box-shadow: 0 10px 25px rgba(0,0,0,0.18);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.18s, transform 0.18s;
  max-width: 90%;
  text-align: center;
  z-index: 10000;
}
#gpe-toast.show{
  opacity: 1;
  transform: translateX(-50%) translateY(-4px);
}
`;

function injectGpeStyles(){
  if (document.getElementById('gpe-inline-css')) return;
  const style=document.createElement('style');
  style.id='gpe-inline-css';
  style.textContent=GPE_INLINE_CSS;
  document.head.appendChild(style);
}

function showToast(msg, ms = 3500){
  let t = document.getElementById('gpe-toast');
  if(!t){ t=document.createElement('div'); t.id='gpe-toast'; document.body.appendChild(t); }
  t.textContent = msg;
  t.classList.add('show');
  clearTimeout(t._hide);
  t._hide = setTimeout(()=> t.classList.remove('show'), ms);
}

function getProductThumb() {
  // 1) Product object, if present
  let url =
    window.product?.featured_image?.src ||
    window.product?.images?.[0]?.src ||
    '';

  // 2) DOM fallbacks (handles lazy-loaded images)
  const imgEl = document.querySelector(
    '.product__media img, .product-gallery img, img[src*="/products/"], img[data-src*="/products/"]'
  );
  if (imgEl) {
    url =
      imgEl.currentSrc ||
      imgEl.src ||
      imgEl.getAttribute('data-src') ||
      imgEl.getAttribute('data-image') ||
      url;
  }

  // Normalize protocol-less //cdn… to https
  if (url && url.startsWith('//')) url = 'https:' + url;
  return url || '';
}
/* -------------------------------------------------------------------------- */
/* 2 · Billing Logic                                                          */
/* -------------------------------------------------------------------------- */
const CORE_UNITS = [
  { key:'Half-Day', label:'½ Day', mult:0.5, hours:5 },   // ← 5 hours now
  { key:'Day',      label:'Day',   mult:1,   hours:24 },
  { key:'Week',     label:'Week',  mult:7,   hours:24*7 },
  { key:'14-Day',   label:'14 Day',mult:14,  hours:24*14 },
  { key:'28-Day',   label:'28 Day',mult:28,  hours:24*28 }
];

function halfDayBranch(s,e,cfg){
  const elapsedH = differenceInMinutes(e,s)/60;
  if (elapsedH <= 5) return flatResult('½ Day',1,cfg);
  if (elapsedH > 5 && elapsedH <= 6){
    const qty = 1 + (elapsedH-5)*0.25;
    return flatResult('½ Day', +qty.toFixed(4), cfg);
  }
  throw new Error('continue-general');
}
function findPrice(label, units){
  const u=units.find(x=>x.label===label);
  if(!u) throw new Error(`Price row "${label}" missing`);
  return u.price;
}
function flatResult(label, qty, cfg){
  const price=findPrice(label, cfg.units);
  return { billableDays:null,
           breakdown:[{unit:label, qty, unitPrice:price, ext:qty*price}],
           total:qty*price };
}
function findCheapestProratedUnit(daysExact,cfg){
  let min=Infinity, best=null;
  for(const unit of cfg.units){
    let q=daysExact/unit.mult;
    if(q<1) q=1;
    const cost=q*unit.price;
    if(cost<min){ min=cost; best={breakdown:[{unit:unit.label, qty:+q.toFixed(2), unitPrice:unit.price, ext:cost}], total:cost}; }
  }
  return best;
}
function computeRentalCharge(startISO,endISO,cfg){
  const s=new Date(startISO), e=new Date(endISO);
  if (e<=s) throw new Error('Return time must be after pick-up');

  const isNextDay = fmtYMD(addDays(s,1))===fmtYMD(e);
  if (isNextDay && s.getHours()>=16 && e.getHours()<=8) return flatResult('½ Day',1,cfg);

  if (holidayWeekendApplied(s,e)) return flatResult('Day',1,cfg)

  if (weekendApplied(s,e)) return flatResult('Day',1,cfg);

  if (isSameDay(s,e)){
    try { return halfDayBranch(s,e,cfg); } catch(_){}
  }
  const minutes=differenceInMinutes(e,s);
  const daysExact=+(minutes/1440).toFixed(3);
  const result=findCheapestProratedUnit(daysExact,cfg);

  const minUnit=cfg.units.find(u=>u.label==='½ Day')||cfg.units[0];
  if (result && result.total < (minUnit?.price||0)) return flatResult(minUnit.label,1,cfg);
  return {...result, billableDays:daysExact};
}

function buildConfigFromMetafield(product) {
  const blob = product?.metafields?.custom?.ProductSpecs ??
               product?.metafields?.ProductSpecs ??
               window.productSpecs;
  if (!blob) throw new Error('ProductSpecs metafield missing');
  const specs = typeof blob === 'string' ? JSON.parse(blob) : blob;
  if (!specs?.prices?.length) throw new Error('ProductSpecs.prices missing');

  // 🔄 NEW: Normalize prices array
  let rawPrices = specs.prices;

  // Convert array-of-objects to array-of-arrays
  if (Array.isArray(rawPrices) && rawPrices.length && typeof rawPrices[0] === 'object' && !Array.isArray(rawPrices[0])) {
    rawPrices = rawPrices.map(p => [
      JSON.parse(p.unit || '""'),
      JSON.parse(p.price || '""')
    ]);
  }
  // Stronger normalizer: lower, NFKC, turn ½ into 1/2, strip everything except a–z, 0–9, and '/'
  const norm = s => String(s)
    .normalize('NFKC')
    .toLowerCase()
    .replace(/½/g, '1/2')
    .replace(/[^a-z0-9/]/g, ''); // removes spaces, dots, dashes, parens, etc.

  const units = [];
  let hasShiftPrices = false;
  const shiftPrices = { day:{}, week:{}, month:{} }; // month = 28-day

  for (const [labelRaw, priceRaw] of specs.prices) {
    if (!labelRaw) continue;
    const raw = norm(labelRaw);
    const price = +String(priceRaw).replace(/[^0-9.]/g, '');
    if (!Number.isFinite(price)) continue;

    // Core (½ Day, Day, Week, 14 Day, 28 Day / Month)
    if (raw === '1/2day')                   { units.push({ ...CORE_UNITS[0], price }); continue; }
    if (raw === 'day')                      { units.push({ ...CORE_UNITS[1], price }); continue; }
    if (raw === 'week')                     { units.push({ ...CORE_UNITS[2], price }); continue; }
    if (raw.includes('14day'))              { units.push({ ...CORE_UNITS[3], price }); continue; }
    if (raw === '28day' || raw === '28days' || raw.includes('28d') ||
        raw === 'month' || raw === 'months' || raw === 'monthly' ||
        raw === 'mnth'  || raw === 'mth'    || raw === 'mths'   || raw === 'mo' || raw === 'mon') {
      units.push({ ...CORE_UNITS[4], price }); continue;
    }

    // Shift variants: 2/3 Shft/Shift + Day/Week/28-day/Month/Mo/Mon/etc.
    const m = raw.match(
      /^(2|3)(?:sh|shf|shft|shift).*?(day|week|28day|28days|28d|mo|mon|mnth|mth|mths|month|months|monthly)$/
    );
    if (m) {
      hasShiftPrices = true;
      const n = m[1];              // "2" or "3"
      const tail = m[2];           // unit tail
      let bucket;
      if (tail === 'week') bucket = 'week';
      else if (tail === 'day') bucket = 'day';
      else bucket = 'month';       // mo / mon / mnth / mth / mths / month(s) / monthly / 28d/28day(s)
      shiftPrices[bucket][n] = price;
      continue;
    }
  }

  if (!units.length) throw new Error('No usable prices found');
  return { units, hasShiftPrices, shiftPrices };
}

/* -------------------------------------------------------------------------- */
/* 3 · Availability API                                                       */
/* -------------------------------------------------------------------------- */
async function checkAvailability(RentId, start, end){
  const url=window.AVAIL_FN_URL;
  if(!url) throw new Error('Availability service not configured');
  const res=await fetch(url,{
    method:'POST',
    headers:{'Content-Type':'application/json','Accept':'application/json'},
    mode:'cors',
    body:JSON.stringify({RentId, start:start.toISOString(), end:end.toISOString()})
  });
  if(!res.ok) throw new Error('Availability endpoint error');
  return res.json();
}

/* -------------------------------------------------------------------------- */
/* 4 · Main Widget                                                            */
/* -------------------------------------------------------------------------- */
class RentalAvailabilityChecker{
  constructor(){
    this.container=document.getElementById('rental-availability-checker');
    if(!this.container) return;
    injectGpeStyles();

    this.state='idle';
    this.buildUI();
    this.cacheEls();
    this._updateRateStrip();
    this.attachEvents();

    // set initial text
    this._updateLabels();
    this._updateNotes();

    setTimeout(()=>{ if(this._allFilled()) this.performAvailabilityCheck(); },150);
  }

  get rentId(){
    return this.container.dataset.sku ||
           window.product?.selected_or_first_available_variant?.sku ||
           window.product?.variants?.[0]?.sku || null;
  }

  _getNextAvailableHour(){
    let next=new Date(); next.setMinutes(0,0,0);
    if(new Date().getMinutes()>0) next.setHours(next.getHours()+1);

    if (isClosedCalendarDay(next)) return nextOpenMorning(addDays(next,0));

    const h=next.getHours();
    if (isSaturday(next)){
      if(h<7) next.setHours(7,0,0,0);
      else if(h>=17) next = nextOpenMorning(addDays(next,1));
    } else if (isSunday(next) || isClosedCalendarDay(next)) {
      next = nextOpenMorning(addDays(next,1));
    } else {
      // Monday-Friday: 6:00 AM to 6:00 PM
      if(h<6) next.setHours(6,0,0,0);
      else if(h>=18) next = nextOpenMorning(addDays(next,1));
    }
    return next;
  }

  _getDefaultReturnDateTime(pickupDate, pickupTime){
    const pickup=new Date(`${pickupDate}T${pickupTime}:00`);
    if (pickup.getDay()===5 && pickup.getHours()>=16) {
      let mon=addDays(pickup, (8 - pickup.getDay())%7); // to Monday
      mon = nextOpenMorning(mon);
      return {date:fmtYMD(mon), time:'08:00'};
    }
    let ret=addDays(pickup,1);
    if (isClosedCalendarDay(ret)) ret = nextOpenMorning(ret);
    return {date:fmtYMD(ret), time: pickupTime};
  }

  buildUI(){
    const next=this._getNextAvailableHour();
    const startDate=fmtYMD(next);
    const startTime=`${String(next.getHours()).padStart(2,'0')}:00`;
    const defRet=this._getDefaultReturnDateTime(startDate,startTime);

    this.container.innerHTML=`
      <div class="gpe-wrap">

        <div class="gpe-label-row">
          <div class="pickup-label">
            <span>Pickup:</span>
            <span class="time-preview pickup-time"></span>
          </div>
          <div class="return-label">
            <span>Return:</span>
            <span class="time-preview return-time"></span>
          </div>
        </div>

        <div class="gpe-fields-row">
          <div class="gpe-field"><input type="date" id="gpe-start-date" value="${startDate}" min="${fmtYMD(new Date())}"></div>
          <div class="gpe-field"><select id="gpe-start-time"></select></div>
          <div class="gpe-field end-field"><input type="date" id="gpe-end-date" value="${defRet.date}" min="${startDate}"></div>
          <div class="gpe-field end-field"><select id="gpe-end-time"></select></div>
        </div>

        <div class="gpe-cta-row">
          <button class="gpe-cta idle" aria-live="polite">Request Reservation</button>
          <div class="gpe-note"></div>
          <div class="gpe-price-full">
            <span class="gpe-price-left"><span class="gpe-price-line1"></span></span>
            <span class="gpe-price-right"><span class="gpe-price-line2"></span></span>
          </div>
        </div>
        <div class="gpe-rate-strip"></div>
        <div class="gpe-shift-matrix"></div>
        <div class="gpe-usage-note">8 hours of machine time per standard workday; overtime rates may apply.</div>
      </div>
    `;

    this._fillTimes('gpe-start-time', startDate, startTime);
    this._fillTimes('gpe-end-time', defRet.date, defRet.time);
  }

  cacheEls(){
    const $=s=>this.container.querySelector(s);
    this.ctaBtn=$('.gpe-cta');
    this.noteEl=$('.gpe-note');
    this.rateStrip=$('.gpe-rate-strip');
    this.matrix=$('.gpe-shift-matrix');

    this.startD=$('#gpe-start-date');
    this.startT=$('#gpe-start-time');
    this.endD=$('#gpe-end-date');
    this.endT=$('#gpe-end-time');

    this.pickupTime=$('.pickup-time');
    this.returnTime=$('.return-time');
  }

  _fillTimes(selectId, ymd, selected){
    const sel=this.container.querySelector('#'+selectId);
    if(!sel) return;
    const d=new Date(ymd+'T12:00:00');
    const hrs=hoursForDate(d);
    sel.innerHTML='';
    if(!hrs){
      // If someone managed to land on a closed day, bump it immediately
      if (selectId==='gpe-start-time'){ this._bumpIfClosedInput('start'); }
      else { this._bumpIfClosedInput('end'); }
      return;
    }
    // Generate time options from open to close
    for(let h=hrs[0]; h<=hrs[1]; h++){
      // For the closing hour, only add :00 if it's not the actual closing time
      if (h === hrs[1]) {
        // Only add the top of the hour, not :30
        const val=`${String(h).padStart(2,'0')}:00`;
        sel.insertAdjacentHTML('beforeend',
          `<option value="${val}" ${val===selected?'selected':''}>${(h%12||12)}:00 ${h<12?'AM':'PM'}</option>`);
      } else {
        // For all other hours, add both :00 and :30
        for(const m of [0,30]){
          const val=`${String(h).padStart(2,'0')}:${m?'30':'00'}`;
          sel.insertAdjacentHTML('beforeend',
            `<option value="${val}" ${val===selected?'selected':''}>${(h%12||12)}:${m?'30':'00'} ${h<12?'AM':'PM'}</option>`);
        }
      }
    }
    if(selected && Array.from(sel.options).some(o=>o.value===selected)) sel.value=selected;
    if (this.pickupTime && this.returnTime) this._updateLabels();
  }

  _updateLabels(){
    if (!this.pickupTime || !this.returnTime) return;
    if(!this._allFilled()) { 
      this.pickupTime.textContent=''; 
      this.returnTime.textContent=''; 
      return; 
    }
    const s=new Date(`${this.startD.value}T${this.startT.value}`);
    const e=new Date(`${this.endD.value}T${this.endT.value}`);
    this.pickupTime.textContent = toDowTime(s);
    this.returnTime.textContent = toDowTime(e);
  }

  _updateNotes(){
    if(!this._allFilled()) { this.noteEl.classList.remove('show'); this.noteEl.textContent=''; return; }
    const s=new Date(`${this.startD.value}T${this.startT.value}`);
    const e=new Date(`${this.endD.value}T${this.endT.value}`);
//half day
  const isNextDay = fmtYMD(addDays(s,1))===fmtYMD(e);
  if (isNextDay && s.getHours()>=16 && e.getHours()<=8){
      this.noteEl.textContent='Evening half-day rate applied - if under 4 machine hours.';
      this.noteEl.classList.add('show'); return;
    }
    
  if (holidayWeekendApplied(s,e)){
      this.noteEl.textContent='Holiday Weekend special rate applied (1 day rate if under 8 machine hours)';
      this.noteEl.classList.add('show'); return;
    }
    if (weekendApplied(s,e)){
      this.noteEl.textContent='Weekend special rate applied (1 day rate if under 8 machine hours)';
      this.noteEl.classList.add('show'); return;
    }
    if (weekendNear(s,e)){
      this.noteEl.textContent='Weekend special is after Fri 4:00 PM to Mon 8:00 AM or before';
      this.noteEl.classList.add('show'); return;
    }
    if (rentalDaysExact(s,e) < 4 && crossesClosedDate(s,e)){
      this.noteEl.textContent='Holiday rate may apply.';
      this.noteEl.classList.add('show'); return;
    }
    this.noteEl.classList.remove('show'); this.noteEl.textContent='';
  }

  _updateRateStrip(){
    let cfg;
    try { cfg=buildConfigFromMetafield(window.product||{}); }
    catch(e){ this.rateStrip.innerHTML=''; return; }

    const order=['½ Day','Day','Week','14 Day','28 Day'];
    const hasShift=cfg.hasShiftPrices;

    const cards = order.map(label=>{
      const u=cfg.units.find(x=>x.label===label);
      if(!u) return '';
      const unitFmt=fmtUnit(label);
      return `
        <div class="rate-card" data-unit="${label}">
          <span class="rate-price">${money0(u.price)}</span>
          <span class="rate-label">
            <span class="txt">${unitFmt}</span>
            ${hasShift && /Day$|Week$|28 Day$/.test(label) ? `<span class="drop" data-toggle-matrix>▾</span>` : ``}
          </span>
        </div>`;
    }).join('');

    this.rateStrip.innerHTML = cards;

    // Clicking a card sets END (+½-day rules)
    this.rateStrip.querySelectorAll('.rate-card').forEach(card=>{
      card.addEventListener('click', ()=> this._applyCardDuration(card.dataset.unit));
      card.querySelectorAll('[data-toggle-matrix]').forEach(el=>{
        el.addEventListener('click',(ev)=>{ ev.preventDefault(); this._toggleMatrix(); ev.stopPropagation(); });
      });
    });

    // Build/refresh matrix content
    if(hasShift){
      const rows=['2','3'].map(n=>`
        <tr>
          <td>${n} Shift</td>
          <td>${cfg.shiftPrices.day?.[n]   ? money0(cfg.shiftPrices.day[n])   : '—'}</td>
          <td>${cfg.shiftPrices.week?.[n]  ? money0(cfg.shiftPrices.week[n])  : '—'}</td>
          <td>${cfg.shiftPrices.month?.[n] ? money0(cfg.shiftPrices.month[n]) : '—'}</td>
        </tr>`).join('');
      this.matrix.innerHTML = `
        <table>
          <thead><tr><th></th><th>Day</th><th>Week</th><th>28 Day</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>`;
    } else {
      this.matrix.innerHTML='';
    }
  }

  _toggleMatrix(){
    if(!this.matrix) return;
    const open=this.matrix.style.display==='block';
    this.matrix.style.display=open?'none':'block';
  }

  _endByUnit(unitLabel, startDate){
    const u=CORE_UNITS.find(u=>u.label===unitLabel);
    if(!u) return addHours(startDate,24);
    return addHours(startDate,u.hours);
  }

  _applyCardDuration(unitLabel){
    if(!this._allFilled()) {
      showToast('Please select pickup date and time first');
      return;
    }
    const s=new Date(`${this.startD.value}T${this.startT.value}`);
    let e;
    let bumped = false;

    // ½-Day after 4pm → next open morning 8:00 AM
    if (/^½/.test(fmtUnit(unitLabel)) && s.getHours()>=16){
      e = nextOpenMorning(addDays(s,1));
      showToast(`Half-day after 4 PM ends next morning at 8 AM`);
    } else {
      e = this._endByUnit(unitLabel, s);
      if (isClosedCalendarDay(e)) { 
        e = nextOpenMorning(e); 
        bumped = true; 
      }
    }

    this.endD.value = fmtYMD(e);
    this.endT.value = `${String(e.getHours()).padStart(2,'0')}:${String(e.getMinutes()).padStart(2,'0')}`;
    this._fillTimes('gpe-end-time', this.endD.value, this.endT.value);
    this._updateLabels();
    this._updateNotes();
    if(bumped) showToast(`Closed date — moved to ${toMonDowTime(e)}.`); 
    else if(!/^½/.test(fmtUnit(unitLabel)) || s.getHours()<16) showToast(`Return set to ${toMonDowTime(e)}`);
    if (this._allFilled()) this.performAvailabilityCheck();
  }

  _paintBtn(state, nAvail=null){
    this.state=state;
    const states={
      checking:['Checking…',true,'checking'],
      available:['Reserve — Available ✓',false,'available'],
      unavailable:['Request Reservation',false,'unavailable'],
      idle:['Request Reservation',false,'idle'],
    };
    const [text,disabled,cls]=states[state]||states.idle;
    this.ctaBtn.textContent=text; this.ctaBtn.disabled=disabled; this.ctaBtn.className='gpe-cta '+cls;
    //if(state==='available' && nAvail) this.ctaBtn.title=`${nAvail} units available`; else this.ctaBtn.title='';
  }

  _allFilled(){
    return !!(this.startD?.value && this.endD?.value && this.startT?.value && this.endT?.value);
  }

  _bumpIfClosedInput(which){
    const inp = which==='start'? this.startD : this.endD;
    let d = new Date(inp.value+'T12:00:00');
    if (isClosedCalendarDay(d)) {
      const bumped = nextOpenMorning(d);
      inp.value = fmtYMD(bumped);
      if (which==='end'){ this.endT.value='08:00'; }
      showToast(`Closed date — moved to ${toMonDowTime(bumped)}.`);
      return true;
    }
    return false;
  }

  _validateAndFixReturnDateTime(){
    const pickup=new Date(this.startD.value+'T'+this.startT.value);
    let ret=new Date(this.endD.value+'T'+this.endT.value);

    if (isClosedCalendarDay(ret)) {
      ret = nextOpenMorning(ret);
      this.endD.value = fmtYMD(ret);
      this.endT.value = '08:00';
      showToast(`Return moved to next open day: ${toMonDowTime(ret)}`);
      return true;
    }

    if (ret < pickup){
      let newRet = addDays(pickup,1);
      if (isClosedCalendarDay(newRet)) newRet = nextOpenMorning(newRet);
      this.endD.value = fmtYMD(newRet);
      this.endT.value = `${String(newRet.getHours()).padStart(2,'0')}:${String(newRet.getMinutes()).padStart(2,'0')}`;
      showToast(`Return must be after pickup - adjusted to ${toMonDowTime(newRet)}`);
      return true;
    }
    if (ret.getTime()===pickup.getTime()){
      let newRet=addHours(pickup,5);
      const closeH = isSaturday(pickup)?17:18;
      if (newRet.getHours()>=closeH) newRet = nextOpenMorning(addDays(pickup,1));
      this.endD.value=fmtYMD(newRet);
      this.endT.value=`${String(newRet.getHours()).padStart(2,'0')}:${newRet.getMinutes()?'30':'00'}`;
      showToast(`Minimum rental is 5 hours - adjusted to ${toMonDowTime(newRet)}`);
      return true;
    }
    return false;
  }

  attachEvents(){
    // Start date
    this.startD.addEventListener('change', ()=>{
      const wasClosedBumped = this._bumpIfClosedInput('start');
      if (wasClosedBumped) {
        this._fillTimes('gpe-start-time', this.startD.value, this.startT.value);
      }
      this._fillTimes('gpe-start-time', this.startD.value, this.startT.value);
      this.endD.min=this.startD.value;

      if (!this._hasReturnBeenManuallySet){
        const def=this._getDefaultReturnDateTime(this.startD.value, this.startT.value);
        this.endD.value=def.date; this.endT.value=def.time;
        this._fillTimes('gpe-end-time', this.endD.value, this.endT.value);
      }
      this._validateAndFixReturnDateTime();
      this._updateLabels();
      this._updateNotes();
      
      // Toast notification for date change
      if (!wasClosedBumped) {
        const d = new Date(`${this.startD.value}T12:00:00`);
        showToast(`Pickup date set to ${DOW3[d.getDay()]} ${MONTHS3[d.getMonth()]} ${d.getDate()}`);
      }
      
      if (this._allFilled()) this.performAvailabilityCheck();
    });

    // Start time
    this.startT.addEventListener('change', ()=>{
      const prevEndDate = this.endD.value;
      const prevEndTime = this.endT.value;
      
      if (!this._hasReturnBeenManuallySet){
        const def=this._getDefaultReturnDateTime(this.startD.value, this.startT.value);
        this.endD.value=def.date; this.endT.value=def.time;
        this._fillTimes('gpe-end-time', this.endD.value, this.endT.value);
      }
      this._validateAndFixReturnDateTime();
      this._updateLabels();
      this._updateNotes();
      
      // Toast for time change
      const s = new Date(`${this.startD.value}T${this.startT.value}`);
      showToast(`Pickup time changed to ${to12hm(s)}`);
      
      // If return was auto-adjusted due to weekend special
      if (!this._hasReturnBeenManuallySet && (prevEndDate !== this.endD.value || prevEndTime !== this.endT.value)) {
        if (s.getDay() === 5 && s.getHours() >= 16) {
          showToast(`Weekend special detected - return auto-set to Monday 8 AM`, 4000);
        }
      }
      
      if (this._allFilled()) this.performAvailabilityCheck();
    });

    // End date
    this.endD.addEventListener('change', ()=>{
      this._hasReturnBeenManuallySet=true;
      const wasClosedBumped = this._bumpIfClosedInput('end');
      this._fillTimes('gpe-end-time', this.endD.value, this.endT.value);
      this._validateAndFixReturnDateTime();
      this._updateLabels();
      this._updateNotes();
      
      // Toast for return date change
      if (!wasClosedBumped) {
        const d = new Date(`${this.endD.value}T12:00:00`);
        showToast(`Return date set to ${DOW3[d.getDay()]} ${MONTHS3[d.getMonth()]} ${d.getDate()}`);
      }
      
      if (this._allFilled()) this.performAvailabilityCheck();
    });

    // End time
    this.endT.addEventListener('change', ()=>{
      this._hasReturnBeenManuallySet=true;
      const wasFixed = this._validateAndFixReturnDateTime();
      this._updateLabels();
      this._updateNotes();
      
      // Toast for return time change
      if (!wasFixed) {
        const e = new Date(`${this.endD.value}T${this.endT.value}`);
        showToast(`Return time changed to ${to12hm(e)}`);
      }
      
      if (this._allFilled()) this.performAvailabilityCheck();
    });

    // CTA
    this.ctaBtn.addEventListener('click', ()=>{
      if (this.ctaBtn.disabled) return;
      if (this.state==='available') {
        showToast('Adding to cart...', 2000);
        window.addToRentalCart();
      }
      else if (this.state==='unavailable') {
        showToast('Requesting availability...', 2000);
        window.requestRentalAvailability();
      }
      else this.performAvailabilityCheck();
    });
  }

  async performAvailabilityCheck(){
    if (this.state==='checking' || !this._allFilled()) return;
    if (!this.rentId){
      this._paintBtn('unavailable');
      return;
    }

    const sISO=`${this.startD.value}T${this.startT.value}`;
    const eISO=`${this.endD.value}T${this.endT.value}`;

    if (new Date(eISO) <= new Date(sISO)){
      this._validateAndFixReturnDateTime();
      return this.performAvailabilityCheck();
    }

    this._paintBtn('checking');
    this._updateNotes();

    // Price
    try{
      const cfg=buildConfigFromMetafield(window.product||{});
      const quote=computeRentalCharge(sISO,eISO,cfg);
      this._updatePriceDisplay(quote);
    }catch(e){ /* ignore */ }

    // Availability
    try{
      const res = window.AVAIL_FN_URL
        ? await checkAvailability(this.rentId, new Date(sISO), new Date(eISO))
        : { isAvailable:true, available:1 };

      if (res?.isAvailable){
        this._paintBtn('available', res.available);
        this._storeSelection(sISO,eISO,res);
      } else {
        this._paintBtn('unavailable');
      }
    }catch(e){
      console.error('Availability check failed:', e);
      this._paintBtn('unavailable');
    }
  }

  // One-line, unit-first, no "× 1.0"
  _updatePriceDisplay(quote){
    const l1=this.container.querySelector('.gpe-price-line1');
    const l2=this.container.querySelector('.gpe-price-line2');
    if(!l1||!l2){return;}
    if(!quote||!quote.breakdown){ l1.textContent=''; l2.textContent=''; return; }

    const first=quote.breakdown[0];
    const unitLabel=fmtUnit(first.unit);
    const qty=Number(first.qty), unitPrice=Number(first.unitPrice), total=Number(quote.total);

    if (Math.abs(qty-1)<1e-9){
      l1.textContent=`Est: ${unitLabel} @ ${money0(unitPrice)}`;
      l2.textContent='';
    } else {
      l1.textContent=`Est: ${unitLabel}${NNBSP}${TIMES}${NNBSP}${qtyFmt(qty)} · ${money0(unitPrice)}`;
      l2.textContent=`= ${money0(total)}`;
    }
  }

_storeSelection(startISO, endISO, avail){
  const thumb = getProductThumb();

  sessionStorage.setItem('currentRentalSelection', JSON.stringify({
    productId: window.product?.id,
    variantId: window.product?.selected_or_first_available_variant?.id,
    sku: this.rentId,
    title: window.product?.title,
    handle: location.pathname.split('/').pop(),
    imageUrl: thumb, // <-- set it here
    rentalDetails: {
      startDate: startISO,
      endDate:   endISO,
      availability: avail,
      checkedAt: new Date().toISOString()
    }
  }));
}
}


/* -------------------------------------------------------------------------- */
/* 5 · Cart Helpers                                                           */
/* -------------------------------------------------------------------------- */


window.addToRentalCart = () => {
  const pick = sessionStorage.getItem('currentRentalSelection');
  if (!pick) { alert('Please check availability first.'); return; }

  const item = JSON.parse(pick);
  item.isAvailable = true;
  if (!item.imageUrl) item.imageUrl = getProductThumb(); // <-- ensure we have one

  const basket = JSON.parse(localStorage.getItem('selectedRentalItems') || '[]');
  const idx = basket.findIndex(i => i.sku === item.sku);

  if (idx >= 0) {
    // keep any existing image if ours is empty, otherwise set it
    basket[idx] = {
      ...basket[idx],
      ...item,
      imageUrl: basket[idx].imageUrl || item.imageUrl
    };
  } else {
    basket.push({ ...item, quantity: 1 });
  }

  localStorage.setItem('selectedRentalItems', JSON.stringify(basket));
  if(document.dispatchEvent(new CustomEvent('gp-rental-reserve',{cancelable:true,detail:{requestIntent:'reservation'}}))) location.href = '/pages/rental-cart';
};


window.requestRentalAvailability = () => {
  const el = document.querySelector('#rental-availability-checker'); if (!el) return;
  const sD = el.querySelector('#gpe-start-date'), sT = el.querySelector('#gpe-start-time');
  const eD = el.querySelector('#gpe-end-date'),   eT = el.querySelector('#gpe-end-time');

  const selection = {
    productId: window.product?.id,
    variantId: window.product?.selected_or_first_available_variant?.id,
    sku: el.dataset.sku || window.product?.selected_or_first_available_variant?.sku,
    title: window.product?.title,
    handle: location.pathname.split('/').pop(),
    imageUrl: getProductThumb(), // <-- add it here too
    rentalDetails: {
      startDate: `${sD.value}T${sT.value}`,
      endDate:   `${eD.value}T${eT.value}`,
      availability: { isAvailable:false, available:0 },
      checkedAt: new Date().toISOString()
    },
    isRequest: true
  };

  const basket = JSON.parse(localStorage.getItem('selectedRentalItems') || '[]');
  const idx = basket.findIndex(i => i.sku === selection.sku);
  if (idx >= 0) basket[idx] = { ...basket[idx], ...selection, imageUrl: basket[idx].imageUrl || selection.imageUrl };
  else basket.push({ ...selection, quantity: 1 });

  localStorage.setItem('selectedRentalItems', JSON.stringify(basket));
  if(document.dispatchEvent(new CustomEvent('gp-rental-reserve',{cancelable:true,detail:{requestIntent:'reservation'}}))) location.href = '/pages/rental-cart';
};


/* -------------------------------------------------------------------------- */
/* 6 · Init                                                                   */
/* -------------------------------------------------------------------------- */
document.addEventListener('DOMContentLoaded', ()=>{ new RentalAvailabilityChecker(); });